import moment from "moment-timezone"
import {AttendanceEventType} from "types/attendance"

export type ValidationError = {
    message: string
    eventIds: (string | number)[]
}

function timeOfDay(m: moment.Moment): number {
    return m.hours() * 60 + m.minutes()
}

export function validateAttendanceEvents(events: any[]): ValidationError[] {
    const errors: ValidationError[] = []
    const rawEvents = (events ?? []).filter((e: any) => !e.deletedAt)
    const sortedEvents = rawEvents
        .filter((e: any) => e.status === "success" && e.eventType && e.createdAt)
        .sort((a: any, b: any) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf())

    const checkin = sortedEvents.find((e: any) => e.eventType === AttendanceEventType.Checkin)
    const checkout = sortedEvents.find((e: any) => e.eventType === AttendanceEventType.Checkout)

    if (!checkin && rawEvents.length > 0) {
        errors.push({
            message: "Check-In is required",
            eventIds: rawEvents.map((e: any) => e.id).filter((id: any) => id != null)
        })
    }

    if (checkin && checkout && timeOfDay(moment(checkout.createdAt)) < timeOfDay(moment(checkin.createdAt))) {
        errors.push({
            message: "Check-Out cannot be before Check-In",
            eventIds: [checkout.id]
        })
    }

    if (checkin) {
        const checkinTOD = timeOfDay(moment(checkin.createdAt))
        for (const e of sortedEvents) {
            if (e.id === checkin.id) continue
            if (e.eventType === AttendanceEventType.Checkin) continue
            if (timeOfDay(moment(e.createdAt)) < checkinTOD) {
                if (e.eventType === AttendanceEventType.BreakStart) {
                    errors.push({
                        message: "Break Out cannot be before Check-In",
                        eventIds: [e.id]
                    })
                } else if (e.eventType === AttendanceEventType.BreakEnd) {
                    errors.push({
                        message: "Break In cannot be before Check-In",
                        eventIds: [e.id]
                    })
                }
            }
        }
    }

    if (checkout) {
        const checkoutTOD = timeOfDay(moment(checkout.createdAt))
        for (const e of sortedEvents) {
            if (e.id === checkout.id) continue
            if (e.eventType === AttendanceEventType.Checkout) continue
            if (timeOfDay(moment(e.createdAt)) > checkoutTOD) {
                if (e.eventType === AttendanceEventType.BreakStart) {
                    errors.push({
                        message: "Break Out cannot be after Check-Out",
                        eventIds: [e.id]
                    })
                } else if (e.eventType === AttendanceEventType.BreakEnd) {
                    errors.push({
                        message: "Break In cannot be after Check-Out",
                        eventIds: [e.id]
                    })
                }
            }
        }
    }

    const breakEvents = sortedEvents.filter(
        (e: any) => e.eventType === AttendanceEventType.BreakStart || e.eventType === AttendanceEventType.BreakEnd
    )
    const stack: any[] = []
    for (let i = 0; i < breakEvents.length; i++) {
        const e = breakEvents[i]
        const prev = breakEvents[i - 1]
        if (prev && e.eventType === prev.eventType) {
            if (e.eventType === AttendanceEventType.BreakStart) {
                errors.push({
                    message: "Break Out cannot be consecutive",
                    eventIds: [e.id]
                })
            } else {
                errors.push({
                    message: "Break In cannot be consecutive",
                    eventIds: [e.id]
                })
            }
        }
        if (e.eventType === AttendanceEventType.BreakStart) {
            stack.push(e)
        } else {
            if (stack.length === 0) {
                const remainingStart = breakEvents
                    .slice(breakEvents.indexOf(e) + 1)
                    .some((x: any) => x.eventType === AttendanceEventType.BreakStart)
                errors.push({
                    message: remainingStart ? "Break In cannot be before Break Out" : "Break In without Break Out",
                    eventIds: [e.id]
                })
            } else {
                const start = stack.pop()
                if (timeOfDay(moment(e.createdAt)) < timeOfDay(moment(start.createdAt))) {
                    errors.push({
                        message: "Break In cannot be before Break Out",
                        eventIds: [e.id]
                    })
                }
            }
        }
    }
    if (stack.length > 0 && checkout) {
        errors.push({
            message: "Break In is required when Check-Out exists",
            eventIds: stack.map((e) => e.id)
        })
    }

    for (let i = 1; i < sortedEvents.length; i++) {
        if (timeOfDay(moment(sortedEvents[i].createdAt)) === timeOfDay(moment(sortedEvents[i - 1].createdAt))) {
            errors.push({
                message: "Events cannot have the same time",
                eventIds: [sortedEvents[i].id, sortedEvents[i - 1].id]
            })
        }
    }

    return errors
}

export function buildEventErrorMap(errors: ValidationError[]): Map<string | number, string[]> {
    const map = new Map<string | number, string[]>()
    for (const err of errors) {
        for (const id of err.eventIds) {
            if (id == null) continue
            const existing = map.get(id) ?? []
            existing.push(err.message)
            map.set(id, existing)
        }
    }
    return map
}
